"use client";

import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { track } from "@vercel/analytics/react";
import { useEffect, useCallback } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { v4 as uuidv4 } from "uuid";

// Session management
const getSessionId = () => {
    if (typeof window === "undefined") return undefined;
    let sessionId = sessionStorage.getItem("bayvalley_session_id");
    if (!sessionId) {
        sessionId = uuidv4();
        sessionStorage.setItem("bayvalley_session_id", sessionId);
    }
    return sessionId;
};

export function useAnalytics() {
    const trackEventMutation = useMutation(api.analytics.trackEvent);
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const getUtmData = useCallback(() => {
        if (typeof window === "undefined") return undefined;
        return {
            source: searchParams.get("utm_source") || undefined,
            medium: searchParams.get("utm_medium") || undefined,
            campaign: searchParams.get("utm_campaign") || undefined,
            content: searchParams.get("utm_content") || undefined,
            term: searchParams.get("utm_term") || undefined,
        };
    }, [searchParams]);

    const logEvent = useCallback(async (
        dogId: string | undefined, 
        type: string, 
        metadata: any = {}
    ) => {
        try {
            const utm = getUtmData();
            const sessionId = getSessionId();
            
            const payload = {
                type,
                dogId,
                sessionId,
                url: typeof window !== "undefined" ? window.location.href : undefined,
                referrer: typeof document !== "undefined" ? document.referrer : undefined,
                utm: utm?.source ? utm : undefined,
                device: typeof navigator !== "undefined" ? {
                    browser: navigator.userAgent,
                    os: navigator.platform,
                    type: /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop",
                } : undefined,
                metadata: {
                    ...metadata,
                    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
                },
            };

            await trackEventMutation(payload);

            // Send to Vercel Analytics for Funnel Visualization
            track(type, { ...payload.metadata, dogId, sessionId });
        } catch (error) {
            console.error("Failed to track event:", error);
        }
    }, [trackEventMutation, getUtmData]);

    // Auto-track page views
    useEffect(() => {
        if (pathname) {
            logEvent(undefined, "page_view", { path: pathname });
        }
    }, [pathname, logEvent]);

    return { logEvent };
}